<script setup>
import { getProjectId } from '@/utils/cdms'
import { useSnackBar } from '@/utils/stores/snackbar'
import { VDataTable } from 'vuetify/labs/VDataTable'

definePage({
  meta: {
    action: 'read',
    subject: 'rtms',
  },
})

const snackbar = useSnackBar()
const projectId = getProjectId()

// 소수점 포맷팅
const decimalType = ref('integer')
const formatQty = (val) => {
  if (val == null || val === '') return '-'
  const num = Number(val)
  if (isNaN(num)) return val
  if (decimalType.value === 'decimal1') return num.toFixed(1)
  if (decimalType.value === 'decimal2') return num.toFixed(2)
  return String(Math.floor(num))
}

// decimalType에 따라 소수점 입력 제한
const onDecimalInput = (e) => {
  const maxDecimals = decimalType.value === 'integer' ? 0 : decimalType.value === 'decimal1' ? 1 : 2
  const allowed = maxDecimals > 0 ? /[0-9.]/ : /[0-9]/
  if (!allowed.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
    e.preventDefault()
    return
  }
  const target = e.target
  const val = target.value
  if (maxDecimals === 0 && e.key === '.') {
    e.preventDefault()
    return
  }
  if (e.key === '.' && val.includes('.')) {
    e.preventDefault()
    return
  }
  const dotIdx = val.indexOf('.')
  if (dotIdx !== -1 && target.selectionStart > dotIdx && val.length - dotIdx > maxDecimals && !['Backspace', 'Delete'].includes(e.key)) {
    e.preventDefault()
  }
}

// ─────────────────────────────────────────
// ① 등록가능 동물수 및 배송요청 (상단 테이블)
// ─────────────────────────────────────────
const siteRows = ref([])
const siteLoading = ref(false)
const threshold = ref(0)

const siteHeaders = [
  { title: '기관번호',           key: 'site_no',         width: '100px', align: 'center' },
  { title: '기관명',             key: 'site_name',       width: '160px' },
  { title: '전체 약물 재고량',   key: 'total_stock',     width: '140px', align: 'center' },
  { title: '등록가능 동물 수',   key: 'available_count', width: '160px', align: 'center' },
  { title: '마지막 업데이트',    key: 'last_updated',    width: '160px', align: 'center' },
  { title: '',                   key: 'supply_action',   width: '110px', align: 'center', sortable: false },
]

const loadSiteInventory = async () => {
  siteLoading.value = true
  try {
    const { data, error } = await useApi(createUrl('/drug-management/inventory/sites', {
      query: { projectId },
    }))
    if (!error.value && data.value) {
      siteRows.value = data.value.sites ?? []
      threshold.value = data.value.threshold ?? 0
      if (data.value.decimalType) decimalType.value = data.value.decimalType
    }
  } catch (e) {
    console.error('사이트 재고 로드 실패:', e)
  } finally {
    siteLoading.value = false
  }
}

// 상태 판정: 군별 최소 재고량(약물량) vs 한계재고량 — 같은 단위로 비교
const getCountStatus = (minGroupStock) => {
  if (threshold.value <= 0) return { color: 'success', label: '충분', sublabel: '' }
  if (minGroupStock >= threshold.value * 2) return { color: 'success', label: '충분', sublabel: '(2배 이상)' }
  if (minGroupStock >= threshold.value)     return { color: 'warning', label: '주의', sublabel: '(1~2배)' }
  return                                           { color: 'error',   label: '부족', sublabel: '(1배 이하)' }
}

// ─────────────────────────────────────────
// 지급 요청 다이얼로그
// ─────────────────────────────────────────
const isSupplyDialogOpen = ref(false)
const supplyTargetSite = ref(null)
const supplyForm = reactive({
  reason: '',
  memo: '',
  quantity: '',
  desiredDate: '',
})

const reasonOptions = [
  { value: '긴급 등록 증가', title: '긴급 등록 증가' },
  { value: '파손·분실',      title: '파손·분실' },
  { value: '기타',           title: '기타 (직접 입력)' },
]

const openSupplyDialog = (item) => {
  supplyTargetSite.value = item
  Object.assign(supplyForm, { reason: '', memo: '', quantity: '', desiredDate: '' })
  isSupplyDialogOpen.value = true
}

const submitSupply = async () => {
  if (!supplyForm.reason) {
    snackbar.showSnackBar('요청 사유를 선택해주세요.', 'error')
    return
  }
  if (!supplyForm.memo || !supplyForm.memo.trim()) {
    snackbar.showSnackBar('기타 요청사항을 입력해주세요.', 'error')
    return
  }
  if (!supplyForm.quantity || Number(supplyForm.quantity) <= 0) {
    snackbar.showSnackBar('요청량을 입력해주세요.', 'error')
    return
  }

  try {
    await $api('/drug-management/supply-requests', {
      method: 'POST',
      body: {
        projectId,
        siteId: supplyTargetSite.value.site_id,
        reasonCategory: supplyForm.reason,
        reasonDetail: supplyForm.memo,
        requestedQuantity: Number(supplyForm.quantity),
        desiredDate: supplyForm.desiredDate || null,
      },
    })
    snackbar.showSnackBar('지급 요청이 등록되었습니다.', 'success')
    isSupplyDialogOpen.value = false
  } catch (e) {
    snackbar.showSnackBar('지급 요청 등록에 실패했습니다.', 'error')
  }
}

// ─────────────────────────────────────────
// ② 동물병원별 DRUG No. 재고 현황 (하단 테이블)
// ─────────────────────────────────────────
const drugStockRows = ref([])
const drugStockLoading = ref(false)

const drugStockHeaders = [
  { title: 'DRUG No.',    key: 'drug_no',       width: '130px' },
  { title: '기관번호',    key: 'site_no',       width: '100px', align: 'center' },
  { title: '기관명',      key: 'site_name',     width: '160px' },
  { title: '현재 재고량', key: 'current_stock',  width: '110px', align: 'center' },
  { title: '배정상태',    key: 'status',        width: '130px', align: 'center' },
  { title: '상태 변경',   key: 'status_action', width: '110px', align: 'center', sortable: false },
]

const filterSite = ref('')
const filterDrugNo = ref('')

const siteFilterOptions = computed(() => [
  { value: '', title: '전체' },
  ...siteRows.value.map(s => ({ value: s.site_id, title: s.site_name })),
])

const drugNoFilterOptions = computed(() => {
  const nos = [...new Set(drugStockRows.value.map(d => d.drug_no))].sort()
  return [
    { value: '', title: '전체' },
    ...nos.map(n => ({ value: n, title: n })),
  ]
})

const loadDrugStock = async () => {
  drugStockLoading.value = true
  try {
    const query = { projectId }
    if (filterSite.value) query.siteId = filterSite.value
    if (filterDrugNo.value) query.drugNo = filterDrugNo.value

    const { data, error } = await useApi(createUrl('/drug-management/inventory/drugs', { query }))
    if (!error.value && data.value) {
      if (data.value.decimalType) decimalType.value = data.value.decimalType
      const items = data.value.items ?? data.value
      drugStockRows.value = items.map(d => ({
        id: d.id,
        drug_no: d.drug_no,
        site_id: d.site_id,
        site_no: d.site_no,
        site_name: d.site_name,
        current_stock: d.current_stock ?? d.quantity,
        status: d.status === 'AVAILABLE' ? 'Available' : 'Unavailable',
        raw_status: d.status,
      }))
    }
  } catch (e) {
    console.error('재고 로드 실패:', e)
  } finally {
    drugStockLoading.value = false
  }
}

const searchDrugStock = () => {
  loadDrugStock()
}

const resetDrugFilter = () => {
  filterSite.value = ''
  filterDrugNo.value = ''
  loadDrugStock()
}

// ─────────────────────────────────────────
// 엑셀 다운로드 (이메일 발송)
// ─────────────────────────────────────────
const downloadExcel = async () => {
  try {
    const body = {
      projectId,
      exportType: 'inventory_drugs',
    }
    if (filterSite.value) body.siteId = filterSite.value
    if (filterDrugNo.value) body.drugNo = filterDrugNo.value

    await $api('/drug-management/export', { method: 'POST', body })
    snackbar.showSnackBar('요청이 완료되었습니다. 결과는 이메일로 전달됩니다.', 'success')
  } catch (e) {
    snackbar.showSnackBar('엑셀 다운로드 요청에 실패했습니다.', 'error')
  }
}

// ─────────────────────────────────────────
// 상태 변경 다이얼로그
// ─────────────────────────────────────────
const isStatusDialogOpen = ref(false)
const statusTarget = ref(null)
const statusForm = reactive({
  new_status: '',
  change_reason: '',
  change_detail: '',
  stock_deduction: '',
})

const changeReasonOptions = [
  { value: '파손',           title: '파손' },
  { value: '유효기간 만료',  title: '유효기간 만료' },
  { value: '반납',           title: '반납' },
  { value: '기타',           title: '기타' },
]

const openStatusDialog = (item) => {
  statusTarget.value = item
  Object.assign(statusForm, {
    new_status:    item.status,
    change_reason: '',
    change_detail: '',
    stock_deduction: '',
  })
  isStatusDialogOpen.value = true
}

const submitStatus = async () => {
  if (!statusForm.change_reason) {
    snackbar.showSnackBar('변경 사유를 선택해주세요.', 'error')
    return
  }

  const newRawStatus = statusForm.new_status === 'Available' ? 'AVAILABLE' : 'UNAVAILABLE'

  try {
    await $api('/drug-management/inventory/status-change', {
      method: 'POST',
      body: {
        drugId: statusTarget.value.id,
        newStatus: newRawStatus,
        reasonCategory: statusForm.change_reason,
        reasonDetail: statusForm.change_detail,
        stockDeduction: statusForm.stock_deduction ? Number(statusForm.stock_deduction) : 0,
      },
    })
    snackbar.showSnackBar('상태가 변경되었습니다.', 'success')
    isStatusDialogOpen.value = false
    await loadDrugStock()
    await loadSiteInventory()
  } catch (e) {
    snackbar.showSnackBar('상태 변경에 실패했습니다.', 'error')
  }
}

// ─────────────────────────────────────────
// 상태 변경 이력
// ─────────────────────────────────────────
const isHistoryDialogOpen = ref(false)
const historyRows = ref([])
const historyLoading = ref(false)

const changeTypeLabel = {
  MANUAL: '수동',
  ASSIGNMENT: '군배정',
  CANCELLATION: '군배정 취소',
}

const historyHeaders = [
  { title: 'DRUG No.',  key: 'drug_no',          width: '120px' },
  { title: '이전 상태', key: 'previous_status',  width: '100px', align: 'center' },
  { title: '변경 상태', key: 'new_status',       width: '100px', align: 'center' },
  { title: '변경 유형', key: 'change_type',      width: '100px', align: 'center' },
  { title: '사유',      key: 'reason_category',  width: '100px' },
  { title: '상세',      key: 'reason_detail',    width: '200px' },
  { title: '변경일',    key: 'created_at',       width: '150px', align: 'center' },
]

const openHistoryDialog = async () => {
  historyLoading.value = true
  isHistoryDialogOpen.value = true
  try {
    const { data, error } = await useApi(createUrl('/drug-management/inventory/status-history', {
      query: { projectId },
    }))
    if (!error.value && data.value) {
      historyRows.value = data.value
    }
  } catch (e) {
    console.error('이력 로드 실패:', e)
  } finally {
    historyLoading.value = false
  }
}

// ─────────────────────────────────────────
// 초기 로드
// ─────────────────────────────────────────
onMounted(async () => {
  await Promise.all([loadSiteInventory(), loadDrugStock()])
})

// ─────────────────────────────────────────
// 다이얼로그 닫기: persistent 유지 + 수동 ESC/바깥클릭 처리
// ─────────────────────────────────────────
let clickStartedOnFlatpickr = false

const onDocPointerDown = (e) => {
  clickStartedOnFlatpickr = !!e.target.closest('.flatpickr-calendar')
}

const onDocKeyDown = (e) => {
  if (e.key !== 'Escape') return
  if (document.querySelector('.flatpickr-calendar.open')) return
  if (isHistoryDialogOpen.value) { isHistoryDialogOpen.value = false; return }
  if (isStatusDialogOpen.value) { isStatusDialogOpen.value = false; return }
  if (isSupplyDialogOpen.value) { isSupplyDialogOpen.value = false; return }
}

const onSupplyOutsideClick = () => {
  if (clickStartedOnFlatpickr) { clickStartedOnFlatpickr = false; return }
  if (document.querySelector('.flatpickr-calendar.open')) return
  isSupplyDialogOpen.value = false
}

const onStatusOutsideClick = () => {
  if (clickStartedOnFlatpickr) { clickStartedOnFlatpickr = false; return }
  if (document.querySelector('.flatpickr-calendar.open')) return
  isStatusDialogOpen.value = false
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointerDown, true)
  document.addEventListener('keydown', onDocKeyDown, true)
})

onUnmounted(() => {
  document.removeEventListener('pointerdown', onDocPointerDown, true)
  document.removeEventListener('keydown', onDocKeyDown, true)
})
</script>

<template>
  <div>
    <!-- ══════════════════════════════════════════════ -->
    <!-- ① 등록가능 동물수 및 배송요청                      -->
    <!-- ══════════════════════════════════════════════ -->
    <VCard class="mb-6">
      <VCardText>
        <div class="d-flex align-center gap-1 mb-1">
          <span class="section-num">1</span>
          <span class="text-h6 font-weight-bold">등록 가능 동물 수 및 배송 요청</span>
        </div>
        <p class="text-caption text-medium-emphasis mb-4">
          등록가능 동물 수는 사용 가능한 재고(Available)를 기준으로 계산됩니다.
        </p>

        <VDataTable
          :items="siteRows"
          :headers="siteHeaders"
          :loading="siteLoading"
          density="compact"
          class="rtms-table"
          :items-per-page="-1"
          hide-default-footer
        >
          <template #item.total_stock="{ item }">
            {{ formatQty(item.total_stock) }}
          </template>

          <template #item.available_count="{ item }">
            <div class="d-flex align-center justify-center gap-2">
              <span
                class="text-subtitle-1 font-weight-bold"
                :class="`text-${getCountStatus(item.min_group_stock ?? item.total_stock).color}`"
              >
                {{ item.available_count }} 마리
              </span>
              <VChip
                :color="getCountStatus(item.min_group_stock ?? item.total_stock).color"
                size="x-small"
                label
              >
                {{ getCountStatus(item.min_group_stock ?? item.total_stock).label }}
                {{ getCountStatus(item.min_group_stock ?? item.total_stock).sublabel }}
              </VChip>
            </div>
          </template>

          <template #item.supply_action="{ item }">
            <VBtn variant="outlined" color="primary" size="small" @click="openSupplyDialog(item)">
              지급 요청
            </VBtn>
          </template>

          <template #bottom>
            <div class="d-flex gap-6 pa-3 text-caption text-medium-emphasis">
              <span>
                <VIcon icon="tabler-circle-filled" size="10" color="success" class="me-1" />
                충분 (2배 이상)
              </span>
              <span>
                <VIcon icon="tabler-circle-filled" size="10" color="warning" class="me-1" />
                주의 (1~2배)
              </span>
              <span>
                <VIcon icon="tabler-circle-filled" size="10" color="error" class="me-1" />
                부족 (1배 이하)
              </span>
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- ══════════════════════════════════════════════ -->
    <!-- ② 동물병원별 DRUG No. 재고 현황                  -->
    <!-- ══════════════════════════════════════════════ -->
    <VCard>
      <VCardText>
        <div class="d-flex justify-space-between align-center mb-4">
          <div class="d-flex align-center gap-1">
            <span class="section-num">2</span>
            <span class="text-h6 font-weight-bold">동물병원별 DRUG No. 재고 현황</span>
          </div>
          <VBtn variant="outlined" size="small" prepend-icon="tabler-file-spreadsheet" @click="downloadExcel">
            엑셀 다운로드
          </VBtn>
        </div>

        <!-- 필터 -->
        <VRow class="mb-3" align="end">
          <VCol cols="12" md="3">
            <AppSelect
              v-model="filterSite"
              :items="siteFilterOptions"
              label="동물병원"
              clearable
              placeholder="전체"
              hide-details
            />
          </VCol>
          <VCol cols="12" md="3">
            <AppSelect
              v-model="filterDrugNo"
              :items="drugNoFilterOptions"
              label="DRUG No."
              clearable
              placeholder="전체"
              hide-details
            />
          </VCol>
          <VCol cols="12" md="3" class="d-flex gap-2">
            <VBtn @click="searchDrugStock">검색</VBtn>
            <VBtn variant="outlined" @click="resetDrugFilter">초기화</VBtn>
          </VCol>
        </VRow>

        <VDataTable
          :items="drugStockRows"
          :headers="drugStockHeaders"
          :loading="drugStockLoading"
          density="compact"
          class="rtms-table"
          :items-per-page="-1"
          hide-default-footer
        >
          <template #item.current_stock="{ item }">
            {{ formatQty(item.current_stock) }}
          </template>

          <template #item.status="{ item }">
            <VChip
              :color="item.status === 'Available' ? 'success' : 'error'"
              size="small"
              label
            >
              {{ item.status }}
            </VChip>
          </template>

          <template #item.status_action="{ item }">
            <VBtn
              v-if="$can('blind', 'rtms') || $can('open', 'rtms')"
              variant="outlined"
              color="error"
              size="x-small"
              @click="openStatusDialog(item)"
            >
              상태 변경
            </VBtn>
          </template>

          <template #bottom>
            <div class="pa-3">
              <p class="text-caption text-medium-emphasis mb-1">
                • Available 상태의 재고만 전체 약물 재고량 및 등록가능 동물 수 계산에 반영됩니다.
              </p>
              <p class="text-caption text-medium-emphasis mb-3">
                • Unavailable 상태로 변경된 재고는 전체 재고량에서 제외됩니다.
              </p>
              <VBtn variant="text" size="small" prepend-icon="tabler-list-details" @click="openHistoryDialog">
                상태 변경 이력 보기
              </VBtn>
            </div>
          </template>
        </VDataTable>
      </VCardText>
    </VCard>

    <!-- ══════════════════════════════════════════════ -->
    <!-- 지급 요청 다이얼로그                               -->
    <!-- ══════════════════════════════════════════════ -->
    <VDialog v-model="isSupplyDialogOpen" max-width="480" persistent no-click-animation @click:outside="onSupplyOutsideClick">
      <VCard title="지급 요청">
        <template #append>
          <VBtn icon variant="text" size="small" @click="isSupplyDialogOpen = false">
            <VIcon icon="tabler-x" size="18" />
          </VBtn>
        </template>
        <VCardText>
          <VRow>
            <VCol cols="12">
              <div class="text-caption text-medium-emphasis mb-1">요청 사유 (필수)</div>
              <VRadioGroup v-model="supplyForm.reason" inline hide-details>
                <VRadio
                  v-for="opt in reasonOptions"
                  :key="opt.value"
                  :label="opt.title"
                  :value="opt.value"
                />
              </VRadioGroup>
            </VCol>
          </VRow>
          <VRow class="mt-1">
            <VCol cols="12">
              <AppTextarea
                v-model="supplyForm.memo"
                label="기타 요청사항 (필수)"
                placeholder="추가 요청사항을 입력하세요."
                rows="3"
                counter="1000"
                maxlength="1000"
                :rules="[v => !!v && !!v.trim() || '기타 요청사항을 입력해주세요.']"
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol cols="6">
              <AppTextField
                v-model="supplyForm.quantity"
                label="요청량 (필수)"
                type="number"
                :step="decimalType === 'integer' ? 1 : decimalType === 'decimal1' ? 0.5 : 0.25"
                placeholder="숫자만 입력하세요."
                @keydown="onDecimalInput"
              />
            </VCol>
            <VCol cols="6">
              <AppDateTimePicker
                v-model="supplyForm.desiredDate"
                label="희망 도착일"
                placeholder="YYYY-MM-DD"
                :config="{ dateFormat: 'Y-m-d' }"
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="justify-end pa-4 gap-2">
          <VBtn color="primary" @click="submitSupply">요청하기</VBtn>
          <VBtn variant="outlined" @click="isSupplyDialogOpen = false">취소</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ══════════════════════════════════════════════ -->
    <!-- 상태 변경 다이얼로그                               -->
    <!-- ══════════════════════════════════════════════ -->
    <VDialog v-model="isStatusDialogOpen" max-width="440" persistent no-click-animation @click:outside="onStatusOutsideClick">
      <VCard title="상태 변경">
        <template #append>
          <VBtn icon variant="text" size="small" @click="isStatusDialogOpen = false">
            <VIcon icon="tabler-x" size="18" />
          </VBtn>
        </template>
        <VCardText>
          <div v-if="statusTarget" class="d-flex align-center gap-3 mb-5">
            <div>
              <div class="text-caption text-medium-emphasis mb-1">현재 상태</div>
              <VChip :color="statusTarget.status === 'Available' ? 'success' : 'error'" size="small" label>
                {{ statusTarget.status }}
              </VChip>
            </div>
            <VIcon icon="tabler-arrow-right" color="medium-emphasis" />
            <div>
              <div class="text-caption text-medium-emphasis mb-1">변경할 상태 선택</div>
              <VRadioGroup v-model="statusForm.new_status" inline hide-details>
                <VRadio label="Available (사용 가능)" value="Available" />
                <VRadio label="Unavailable (사용 불가)" value="Unavailable" />
              </VRadioGroup>
            </div>
          </div>

          <VRow>
            <VCol cols="12">
              <AppSelect
                v-model="statusForm.change_reason"
                :items="changeReasonOptions"
                label="변경 사유 (필수)"
                placeholder="사유를 선택하세요."
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol cols="12">
              <AppTextarea
                v-model="statusForm.change_detail"
                label="상세 사유"
                placeholder="상세 사유를 입력하세요."
                rows="3"
                counter="1000"
                maxlength="1000"
              />
            </VCol>
          </VRow>
          <VRow>
            <VCol cols="6">
              <AppTextField
                v-model="statusForm.stock_deduction"
                label="재고 차감 개수"
                type="number"
                placeholder="0"
                min="0"
                :step="decimalType === 'integer' ? 1 : decimalType === 'decimal1' ? 0.5 : 0.25"
                hide-details
              />
              <p class="text-caption text-medium-emphasis mt-1">
                입력한 개수만큼 해당 기관의 재고량이 차감됩니다.
              </p>
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="justify-end pa-4 gap-2">
          <VBtn color="primary" @click="submitStatus">변경하기</VBtn>
          <VBtn variant="outlined" @click="isStatusDialogOpen = false">취소</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- ══════════════════════════════════════════════ -->
    <!-- 상태 변경 이력 다이얼로그                          -->
    <!-- ══════════════════════════════════════════════ -->
    <VDialog v-model="isHistoryDialogOpen" max-width="900" persistent no-click-animation @click:outside="isHistoryDialogOpen = false">
      <VCard title="상태 변경 이력">
        <template #append>
          <VBtn icon variant="text" size="small" @click="isHistoryDialogOpen = false">
            <VIcon icon="tabler-x" size="18" />
          </VBtn>
        </template>
        <VCardText>
          <VDataTable
            :items="historyRows"
            :headers="historyHeaders"
            :loading="historyLoading"
            density="compact"
            class="rtms-table"
            :items-per-page="20"
          >
            <template #item.previous_status="{ item }">
              <VChip :color="item.previous_status === 'AVAILABLE' ? 'success' : 'error'" size="x-small" label>
                {{ item.previous_status }}
              </VChip>
            </template>
            <template #item.new_status="{ item }">
              <VChip :color="item.new_status === 'AVAILABLE' ? 'success' : 'error'" size="x-small" label>
                {{ item.new_status }}
              </VChip>
            </template>
            <template #item.change_type="{ item }">
              {{ changeTypeLabel[item.change_type] ?? item.change_type }}
            </template>
            <template #item.created_at="{ item }">
              {{ item.created_at ? new Date(item.created_at).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : '' }}
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VDialog>

  </div>
</template>

<style scoped>
.section-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: rgb(var(--v-theme-primary));
  color: white;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.rtms-table :deep(th),
.rtms-table :deep(td) {
  white-space: nowrap;
  font-size: 13px;
  padding: 6px 8px !important;
}

.rtms-table :deep(th) {
  font-size: 12px;
  font-weight: 600;
}

.rtms-table :deep(tr) {
  cursor: default;
}

.rtms-table :deep(td) {
  color: inherit;
  text-decoration: none;
}

.rtms-table :deep(td a) {
  color: inherit;
  text-decoration: none;
  pointer-events: none;
}
</style>
